import { CSSProperties, memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { IconHighlight } from '@tabler/icons-react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { parsePxValue } from '../utils';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { updateCaptionStyles } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import { HighlightType, HighlightTypeEnum } from '@/domains/asset';
import { TEXT_COLOR_DEFAULT } from '@/stores/constants';

function CaptionHighlightPopover() {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);

  const { mode, color, highlightBoxInset } = useMemo(() => {
    const highlightType = clipData.asset_metadata.caption_styles?.wordStyle?.highlightType || HighlightTypeEnum.None;
    const activeWordColor = clipData.asset_metadata.caption_styles?.wordStyle?.active?.color as CSSProperties['color'];
    const highlightBoxBackground = clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle
      ?.background as CSSProperties['background'];

    // Extract inset value from the inset property (assuming uniform horizontal inset)
    const insetValue = clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle?.inset;
    let currentHorizontalInset = 0;
    if (insetValue && typeof insetValue === 'string') {
      // Parse inset value like "-0.15em -0.3em" and extract horizontal value
      const insetParts = insetValue.trim().split(/\s+/);
      if (insetParts.length >= 2) {
        // For inset: "top right bottom left" or "vertical horizontal", take the horizontal (right) value
        const horizontalInset = insetParts[1];
        if (horizontalInset.startsWith('-')) {
          // Convert negative inset to positive value for UI control
          currentHorizontalInset = Math.abs(parsePxValue(horizontalInset)) || 0;
        }
      }
    }

    return {
      mode: highlightType,
      color:
        (highlightType === HighlightTypeEnum.Text ? activeWordColor : highlightBoxBackground) || TEXT_COLOR_DEFAULT,
      highlightBoxInset: currentHorizontalInset || 0
    };
  }, [
    clipData.asset_metadata.caption_styles?.wordStyle?.highlightType,
    clipData.asset_metadata.caption_styles?.wordStyle?.active?.color,
    clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle?.background,
    clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle?.inset
  ]);

  const updateHighlight = useCallback(
    (newValues: { mode?: HighlightType; color?: string; horizontalInset?: number }) => {
      const updatedValues = {
        mode: newValues.mode ?? mode,
        color: newValues.color ?? color,
        horizontalInset: newValues.horizontalInset ?? highlightBoxInset
      };

      if (updatedValues.mode === HighlightTypeEnum.None) {
        updateCaptionStyles(clipId, {
          wordStyle: {
            ...clipData.asset_metadata.caption_styles?.wordStyle,
            highlightType: HighlightTypeEnum.None,
            active: {
              ...clipData.asset_metadata.caption_styles?.wordStyle?.active,
              color: undefined,
              background: undefined
            }
          }
        });
        return;
      }

      if (updatedValues.mode === 'text') {
        updateCaptionStyles(clipId, {
          wordStyle: {
            ...clipData.asset_metadata.caption_styles?.wordStyle,
            highlightType: HighlightTypeEnum.Text,
            active: {
              ...clipData.asset_metadata.caption_styles?.wordStyle?.active,
              color: updatedValues.color as CSSProperties['color'],
              background: undefined
            }
          }
        });
        return;
      }

      if (updatedValues.mode === 'box') {
        updateCaptionStyles(clipId, {
          wordStyle: {
            ...clipData.asset_metadata.caption_styles?.wordStyle,
            highlightType: HighlightTypeEnum.Box,
            active: {
              ...clipData.asset_metadata.caption_styles?.wordStyle?.inactive,
              color: clipData.asset_metadata.caption_styles?.wordStyle?.inactive?.color
            },
            highlightBoxStyle: {
              ...clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle,
              inset: `-${updatedValues.horizontalInset * 0.5}px -${updatedValues.horizontalInset}px`,
              borderRadius: `${updatedValues.horizontalInset * 0.75}px`,
              background: updatedValues.color as CSSProperties['background']
            }
          }
        });
      }
    },
    [mode, color, highlightBoxInset, clipId, clipData.asset_metadata.caption_styles?.wordStyle]
  );

  const onHighlightModeChange = useCallback(
    (value: HighlightType) => {
      updateHighlight({ mode: value });
    },
    [updateHighlight]
  );

  const onHighlightColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateHighlight({ color: value });
      }
    },
    [updateHighlight]
  );

  const onHighlightBoxInsetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(e.target.value);

      updateCaptionStyles(clipId, {
        wordStyle: {
          ...clipData.asset_metadata.caption_styles?.wordStyle,
          highlightBoxStyle: {
            ...clipData.asset_metadata.caption_styles?.wordStyle?.highlightBoxStyle,
            inset: `-${value * 0.5}px -${value}px`,
            borderRadius: `${value * 0.75}px`
          }
        }
      });
    },
    [clipId, clipData.asset_metadata.caption_styles?.wordStyle]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-highlight-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconHighlight size={16} />
              <PlayerMenuTooltip content="Highlight" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="space-y-4 rounded-lg border bg-white p-4 shadow-lg">
                {/* Highlight Mode */}
                <div className="grid grid-cols-12 items-center justify-between gap-2">
                  <span className="col-span-6 text-sm font-medium">Highlight Mode</span>
                  <div className="col-span-6">
                    <select
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      value={mode}
                      onChange={e => onHighlightModeChange(e.target.value as HighlightType)}
                    >
                      <option value="none">None</option>
                      <option value="text">Text Color</option>
                      <option value="box">Box</option>
                    </select>
                  </div>
                </div>

                {/* Box Inset */}
                {mode === HighlightTypeEnum.Box && (
                  <>
                    <div className="grid grid-cols-12 items-center justify-between gap-2">
                      <span className="col-span-3 text-sm font-medium">Size</span>
                      <span className="col-span-9 flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={30}
                          value={highlightBoxInset}
                          step={1}
                          onChange={onHighlightBoxInsetChange}
                          className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                        />
                        <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                          {highlightBoxInset}
                        </span>
                      </span>
                    </div>
                  </>
                )}

                {mode !== 'none' && (
                  <div className="grid grid-cols-12 items-center justify-between gap-2">
                    <span className="col-span-3 text-sm font-medium">Color</span>
                    <ColorPicker
                      defaultColors={brandKitStore.colors}
                      defaultColorsLabel={mode === 'text' ? 'Text Color' : 'Box Color'}
                      onChange={onHighlightColorChange}
                      hex={color as string}
                      children={
                        <div className="flex size-full items-center justify-between bg-white p-2">
                          <div className="size-6 rounded border" style={{ background: color }} />
                          <span className="font-mono text-xs text-slate-400">{color}</span>
                        </div>
                      }
                      classNames="relative col-span-9 flex w-full items-center justify-between rounded-md border overflow-hidden"
                    />
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionHighlightPopover);
